'use client';

import './dialog.scss';

import { PropsWithChildren, ReactNode, forwardRef } from 'react';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';

import {
  ButtonStyle,
  ButtonStyleProps,
} from '../../atoms/button-style/button-style';
import { cn } from '../../utils/helpers';
import {
  Modal,
  ModalClose,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalProps,
  ModalTitle,
  ModalTrigger,
  ModalTriggerProps,
} from '../modal/modal';

export type DialogProps = ModalProps;

export const Dialog = Modal;

/**
 * Dialog trigger.
 */
export type DialogTriggerProps = ModalTriggerProps;
export const DialogTrigger = ModalTrigger;

/**
 * Dialog close.
 */
export const DialogClose = ModalClose;

/**
 * Dialog content.
 */
export type DialogContentProps = ModalContentProps & {
  centered?: boolean;
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  supportingText?: ReactNode;
  purpose?: 'default' | 'neutral' | 'positive' | 'warning' | 'negative';
  customIcon?: ReactNode;
  icon?: IconNames;
};

/**
 * Primary configurable content/props required all Dialogs.
 */
export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      title,
      supportingText,
      size = 'md',
      style,
      purpose = 'neutral',
      customIcon,
      icon,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <ModalContent
        ref={ref}
        aria-describedby={undefined}
        className={cn(
          `Dialog__content Dialog__content--${size} flex flex-col overflow-y-auto`,
        )}
        style={{
          ...style,
        }}
        {...props}
      >
        {/* Header */}
        <header className="flex flex-col">
          {/* Icon and close button */}
          <div
            className={cn(' flex items-center  justify-end pb-comfy-sm', {
              'justify-between': icon || customIcon,
            })}
          >
            {(icon || customIcon) && (
              <div
                className={cn(
                  `Dialog__icon
                Dialog__icon--${purpose} flex items-center justify-center rounded-full`,
                )}
              >
                {customIcon || (icon && <ArkIcon size="24" iconName={icon} />)}
              </div>
            )}

            <ModalCloseButton
              className="Dialog__close mr-comfy-xs mt-comfy-xs"
              name="Dialog"
            />
          </div>
        </header>
        <div className="overflow-y-auto pl-snug-md -ml-snug-md">
          {/* Heading */}
          <div className="pt-comfy-sm mb-comfy-xl">
            <ModalTitle className="Dialog_title heading-sm ">
              {title}
            </ModalTitle>
            {supportingText && (
              <span className="block pt-snug-md body-sm body-neutral-mild">
                {supportingText}
              </span>
            )}
          </div>
          {children}
        </div>
      </ModalContent>
    );
  },
);

/**
 * Optional slot for additional dialog content. Necessary for overflow-y scrolling
 */
export const DialogSlot: React.FC<PropsWithChildren> = ({ children }) => {
  return <div className="overflow-y-auto py-comfy-sm">{children}</div>;
};

/**
 * Footer containing action buttons.
 */
export const DialogActions: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="Dialog__actions">
      <div className="Dialog__actions-container flex flex-row gap-comfy-xs md:justify-end absolute bottom-0 left-0 w-full">
        {children}
      </div>
    </div>
  );
};

/**
 * Dialog's primary CTA.
 */
export const DialogActionPrimary: React.FC<
  Omit<ButtonStyleProps, 'className' | 'size'> & {
    type?: 'button' | 'submit';
    onClick?: () => void;
  }
> = ({ children, type = 'button', ...props }) => {
  return (
    <ButtonStyle className="w-full md:w-fit" size="md" {...props}>
      {/* The explicit ternary is actually required by lint rules */}
      <button type={type === 'submit' ? 'submit' : 'button'}>{children}</button>
    </ButtonStyle>
  );
};

/**
 * Optional footer button to close/cancel the dialog.
 */
export const DialogActionClose: React.FC<PropsWithChildren> = ({
  children,
}) => {
  return (
    <ButtonStyle
      className="w-full md:w-fit"
      size="md"
      purpose="neutral"
      variant="secondary"
    >
      <DialogClose>{children}</DialogClose>
    </ButtonStyle>
  );
};
